import type { RichLead } from '../../lead.popup/types';

import { type ApiErrorResponse, estClient } from './estClient';
import { fixAppeal } from './normalizationFns';

class AppealsApi {
  async getAll(
    params: {
      maxStatus?: AppealStatus;

      /**
       * bitmask
       */
      invalid?: string;
    } = {},
  ): Promise<Appeal[]> {
    const resp = await estClient.get<
      { success: true; appeals: Appeal[] } | ApiErrorResponse
    >('/api/appeal/get/all', {
      query: params,
    });

    if (!resp.success) {
      throw Object.assign(
        new Error(`Error fetching appeals (${resp.code}): ${resp.message}`),
        {
          resp,
        },
      );
    }

    for (const appeal of resp.appeals) {
      fixAppeal(appeal);
    }

    return resp.appeals;
  }

  get APPEAL_STATUS() {
    return APPEAL_STATUS;
  }
}

/**
 * see: application/EstOffice/Appeal/AppealModel.php
 */
const APPEAL_STATUS = Object.freeze({
  NEW: 0,
  UNDERWAY: 1,
  CLOSED: 99,
});

export type AppealStatus = (typeof APPEAL_STATUS)[keyof typeof APPEAL_STATUS];
export interface Appeal {
  id: number;
  status: AppealStatus;

  lead_id: RichLead['id'] | null;
  lead_status_on_create: RichLead['status_id'] | null;
  lead_status_on_close: RichLead['status_id'] | null;

  is_new_lead: 1 | 0;
  is_organic: 1 | 0;
  is_notified: 1 | 0;
  is_invalid: 1 | 0;

  ad_source_type_id: number | null;
  ad_source_id: string | null;

  /**
   * @example '380671112233'
   */
  phone: string;
  name: string | null;
  custom_note: string | null;

  object_id: string | null;
  unit_id: string | null;

  source: string | null;
  source_type: string | null;
  creation_source: number;

  opportunity_id: number | null;
  opportunity_open_status: number | null;
  opportunity_open_stage: number | null;
  opportunity_open_label_id: number | null;
  opportunity_close_status: number | null;
  opportunity_close_stage: number | null;
  opportunity_close_label_id: number | null;

  /**
   * The opportunity's *current* label, unlike opportunity_open_label_id /
   * opportunity_close_label_id above which are point-in-time snapshots.
   * Only the appeal list (/api/appeal/get/all) returns it.
   */
  opportunity_current_label_id?: number | null;

  /**
   * @example '2023-05-12 14:44:17'
   */
  created_at: string;
  created_by: string;

  /**
   * @example '2023-05-12 14:44:17'
   */
  underway_at: string | null;
  underway_by: string | null;

  /**
   * @example '2023-05-12 14:44:17'
   */
  last_action_at: string | null;
  last_action_by: string | null;

  /**
   * @example '2023-05-12 14:44:17'
   */
  closed_at: string | null;
  closed_by: string | null;

  gaClientId: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;

  /**
   * Landing page url
   */
  fullUrl: string | null;

  ad_id: string | null;
  ad_name: string | null;

  adset_id: string | null;
  adset_name: string | null;

  campaign_id: string | null;
  campaign_name: string | null;

  form_id: string | null;
  form_name: string | null;

  platform: string | null;

  fields: {
    id: string;
    appeal_id: string;
    key:
      | 'ad_source'
      | 'ad_source_type'
      | 'custom_fields'
      | 'custom_note'
      | 'fullUrl'
      | 'gaClientId'
      | 'name'
      | 'object_id'
      | 'phone'
      | 'unit_id'
      | 'utm_campaign'
      | 'utm_content'
      | 'utm_medium'
      | 'utm_source'
      | 'utm_term'
      | 'adset_id'
      | 'adset_name'
      | 'ad_id'
      | 'ad_name'
      | 'campaign_id'
      | 'campaign_name'
      | 'form_id'
      | 'form_name'
      | 'is_organic'
      | 'platform'
      | 'source'
      | 'source_type';
    value: string;
  }[];
}

export const appealsApi = new AppealsApi();
